const NMS_PARTICIPANTS_STORAGE_KEY = "martti.nmsParticipants"
const SUPERVISOR_SESSION_STORAGE_KEY = "martti.supervisorSession"

// Which media participants this tab holds, keyed by interaction. An interpreter can be on more
// than one call at a time, so a single entry would let the second call's participant overwrite
// the first and leak it.
// Kept in sessionStorage because it describes one tab: a refresh keeps the same tab and should
// recover it, a new tab is genuinely a new participant. A refresh throws the running client away
// and asks for a new participant, leaving the old one open until the media server times it out
// about forty seconds later, billing twice in between (AE-1352). Remembering the old one lets us
// close it deliberately instead of waiting.

const readAll = () => {
  try {
    const stored = JSON.parse(window.sessionStorage?.getItem(NMS_PARTICIPANTS_STORAGE_KEY) || "{}")
    return stored && typeof stored === "object" ? stored : {}
  } catch (err) {
    // Storage can be unavailable (private mode, blocked cookies). Losing this costs a late
    // segment close, not correctness, so it must never take the call screen down.
    return {}
  }
}

// Writes, or removes when there is nothing to store, and reports whether storage accepted it.
const safeWrite = (key, value) => {
  try {
    if (value === null || value === undefined) {
      window.sessionStorage?.removeItem(key)
    } else {
      window.sessionStorage?.setItem(key, JSON.stringify(value))
    }
    return true
  } catch (err) {
    return false
  }
}

const writeAll = (entries) => safeWrite(NMS_PARTICIPANTS_STORAGE_KEY, entries)

// Only a genuine refresh of this tab leaves a participant of ours behind. A refresh reports
// "reload"; a duplicated or newly opened tab reports "navigate".
export const wasReloaded = () => {
  try {
    const [nav] = window.performance?.getEntriesByType?.("navigation") ?? []
    return nav?.type === "reload"
  } catch (err) {
    return false
  }
}

// Duplicating a tab copies sessionStorage, so a duplicate starts life holding the original tab's
// participants. Closing one of those would drop a session that is live and in use, so anything
// present on a fresh navigation was inherited rather than ours and is dropped. Only entries this
// tab wrote for itself survive to be acted on.
let inheritedEntriesDropped = false

const dropInheritedEntries = () => {
  if (inheritedEntriesDropped) return
  inheritedEntriesDropped = true
  if (!wasReloaded()) {
    writeAll({})
    writeSupervisorSession(null)
  }
}

// Lets a caller discard inherited state up front instead of waiting for the first read, which a
// failed start-up lookup would never reach (AE-1582).
export const discardInheritedEntries = () => dropInheritedEntries()

// Tests only: the flag above is module state and would otherwise leak between cases.
export const _resetInheritedCheck = () => {
  inheritedEntriesDropped = false
}

export const rememberNmsParticipant = ({ interactionId, participantId }) => {
  if (!interactionId || !participantId || participantId === "NA") return false
  dropInheritedEntries()
  const entries = readAll()
  entries[interactionId] = participantId
  return writeAll(entries)
}

export const readNmsParticipant = (interactionId) => {
  if (!interactionId) return null
  dropInheritedEntries()
  return readAll()[interactionId] || null
}

export const forgetNmsParticipant = (interactionId) => {
  if (!interactionId) return writeAll({})
  const entries = readAll()
  delete entries[interactionId]
  return writeAll(entries)
}

// A supervisor who barges or listens in holds no task of their own -- the task belongs to the
// interpreter -- so after a refresh there is no active call to ask the API for and nothing to
// rejoin from. Remember what this tab joined (AE-1582) so the refresh can rejoin it. Unlike the
// participants above there is one entry rather than one per interaction: a supervisor monitors a
// single call at a time.
const writeSupervisorSession = (session) => safeWrite(SUPERVISOR_SESSION_STORAGE_KEY, session)

export const rememberSupervisorSession = ({ mode, call, userId }) => {
  if (!call?.interaction?.id || !mode || !userId) return false
  dropInheritedEntries()
  return writeSupervisorSession({ mode, call, userId })
}

// The session belongs to whoever started monitoring. A different user signing in on the same tab
// must not rejoin it with their own id, so a record that is not theirs is discarded.
const readRawSupervisorSession = () => {
  try {
    return JSON.parse(window.sessionStorage?.getItem(SUPERVISOR_SESSION_STORAGE_KEY) || "null")
  } catch (err) {
    return null
  }
}

export const readSupervisorSession = (userId) => {
  dropInheritedEntries()
  try {
    const stored = readRawSupervisorSession()
    if (!stored?.mode || !stored?.call?.interaction?.id) return null
    if (!userId || stored.userId !== userId) {
      writeSupervisorSession(null)
      return null
    }
    return stored
  } catch (err) {
    return null
  }
}

export const rememberIntakeTakeover = (callSegmentId) => {
  const stored = readRawSupervisorSession()
  if (!stored) return false
  return writeSupervisorSession({ ...stored, tookOverIntake: true, takeoverSegmentId: callSegmentId })
}

export const forgetIntakeTakeover = () => {
  const stored = readRawSupervisorSession()
  if (!stored) return false
  const session = { ...stored }
  delete session.tookOverIntake
  delete session.takeoverSegmentId
  return writeSupervisorSession(session)
}

export const forgetSupervisorSession = () => writeSupervisorSession(null)
